'use client';

import React, { useState } from 'react';
import { ModalIcon } from 'src/components/_common/Modal/index.styled';
import useLoadMore from 'src/hooks/useLoadMore';
import { useGetMainOriginalsListQuery } from 'src/store/server/main/new/main.query';
import { momentTimezone } from 'src/utils/time';

import type { FilterRadioOption } from './FilterRadioGroup/FilterRadioGroup';
import FilterRadioGroup from './FilterRadioGroup/FilterRadioGroup';
import OriginalsCard from './OriginalsCard/OriginalsCard';
import OriginalsSectionStyled from './OriginalsSection.styled';

const ITEMS_PER_PAGE = 5;

type FilterRadioValue = 'all' | 'ongoing' | 'ended';
const FilterRadioOptions: FilterRadioOption<FilterRadioValue>[] = [
  { id: 'all', value: 'all', label: '전체' },
  { id: 'ongoing', value: 'ongoing', label: '진행중' },
  { id: 'ended', value: 'ended', label: '완료' },
];

const OriginalsSection = () => {
  const [filterValue, setFilterValue] = useState<FilterRadioValue>('all');

  const {
    data: mainOriginalsList,
    isLoading: isMainOriginalsLoading,
    isError: isMainOriginalsError,
  } = useGetMainOriginalsListQuery();

  const currentTime = momentTimezone().unix();
  /**
   * 필터링 프로세스 정리
   */
  // 1. 각 필터별로 독립적인 페이지네이션 상태를 관리
  const allList = useLoadMore(mainOriginalsList, ITEMS_PER_PAGE);
  const ongoingList = useLoadMore(
    mainOriginalsList?.filter((item) => currentTime >= item.startDateTime && currentTime <= item.endDateTime),
    ITEMS_PER_PAGE,
  );
  const endedList = useLoadMore(
    mainOriginalsList?.filter((item) => currentTime > item.endDateTime),
    ITEMS_PER_PAGE,
  );

  // 2. filterValue에 따라 현재 보여줄 리스트 결정
  const activeList = (() => {
    switch (filterValue) {
      case 'ongoing':
        return ongoingList;
      case 'ended':
        return endedList;
      default:
        return allList;
    }
  })();

  // 3. activeList에서 필요한 값들 추출
  const { visibleList, hasMore, handleLoadMore } = activeList;

  const handleFilterChange = (value: FilterRadioValue) => {
    setFilterValue(value);
  };

  return (
    <OriginalsSectionStyled.Container>
      {/* 필터 라디오 버튼 */}
      <FilterRadioGroup selectedValue={filterValue} onChange={handleFilterChange} options={FilterRadioOptions} />

      <OriginalsSectionStyled.ListContainer>
        {/* 데이터가 로딩 중이면 Skeleton 표시 */}
        {(isMainOriginalsLoading || isMainOriginalsError) &&
          Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
            <OriginalsSectionStyled.Skeleton key={`skeleton-${index}`} />
          ))}

        {/* 데이터가 로드되면 콘텐츠 표시 */}
        {!isMainOriginalsLoading && !isMainOriginalsError && (
          <>
            {visibleList?.map((originals) => (
              <OriginalsCard
                key={originals.idx}
                originalsIdx={originals.idx}
                title={originals.title}
                subTitle={originals.subTitle}
                image={originals.image}
                startDateTime={originals.startDateTime}
                endDateTime={originals.endDateTime}
                linkUrl={originals.linkUrl}
                blankYN={originals.blankYN}
                myFavorite={originals.myFavorite}
              />
            ))}
            {hasMore && (
              <OriginalsSectionStyled.LoadMoreButton type="button" onClick={handleLoadMore}>
                더보기
              </OriginalsSectionStyled.LoadMoreButton>
            )}
          </>
        )}

        {/* 진행중인데, 데이터가 없으면 메시지 표시 */}
        {filterValue === 'ongoing' && visibleList && visibleList.length === 0 && (
          <OriginalsSectionStyled.EmptyContainer>
            <ModalIcon src="/images/icon/ico_danger.svg" alt="alert icon" width={56} height={56} />
            <OriginalsSectionStyled.EmptyTextWrapper>
              <OriginalsSectionStyled.EmptyTitle>진행 중인 오리지널스가 없습니다.</OriginalsSectionStyled.EmptyTitle>
              <OriginalsSectionStyled.EmptyDescription>
                새로운 오리지널스 준비중
              </OriginalsSectionStyled.EmptyDescription>
            </OriginalsSectionStyled.EmptyTextWrapper>
          </OriginalsSectionStyled.EmptyContainer>
        )}
      </OriginalsSectionStyled.ListContainer>
    </OriginalsSectionStyled.Container>
  );
};

export default OriginalsSection;
